import { useCallback, useEffect, useState, useMemo } from 'react';
import { getOrganizationsListAction } from 'features/organizations/redux/actions';
import { useAppDispatch } from 'shared/services/redux/hooks/use-dispatch';
import { useAppSelector } from 'shared/services/redux/hooks/use-selector';
import {
  getDataIndexedColumns,
  getDefaultTablePagination,
  useTableSearchParams,
} from 'shared/UI/UIKit/Table';
import { useFilteredColumns } from 'common/ColumnsFilter/use-filtered-columns';
import type { AppTableColumn, AppTableState } from 'shared/UI/UIKit/Table';
import type { Organization, OrganizationsListRequest } from 'services/api';
import type { TableFilterField } from 'common/TableFilter';
import { clearState } from '../../redux/slice';
import { getOrganizationsTableColumns } from './columns';
import { organizationsFilterFields } from './filter-fields';

const getInitialStateWithSorters = (columns: AppTableColumn<Organization>[]): AppTableState => {
  return {
    sorters: getDataIndexedColumns(columns)
      .filter(({ key }) => key !== 'controls')
      .map(({ key, dataIndex }) => {
        return key === 'name'
          ? {
              key,
              dataIndex,
              order: 'ascend',
            }
          : {
              key,
              dataIndex,
            };
      }),
    filters: [],
    pagination: getDefaultTablePagination(),
  };
};

export const useOrganizations = () => {
  const dispatch = useAppDispatch();
  const { organizations, total, loading } = useAppSelector((state) => state.organizationsState);
  const [siderVisible, setSiderVisible] = useState(false);
  const [filterFields, setFilterFields] = useState<TableFilterField[]>(organizationsFilterFields);

  const columns = useMemo(() => getOrganizationsTableColumns(), []);
  const { filteredColumns, hiddenColumns, visibleKeys, setVisibleKeys } =
    useFilteredColumns(columns);

  const getTableItems = useCallback(
    (nextTableState: AppTableState, nextFilterState: Record<string, any>) => {
      const {
        pagination: { current, pageSize },
        sorters,
      } = nextTableState;
      const sort = sorters
        .filter((col: any) => !!col.order)
        .map((col: any) => `${col.dataIndex.join('.')},${col.order === 'ascend' ? 'asc' : 'desc'}`);

      const apiParams: OrganizationsListRequest = {
        page: current! - 1,
        size: pageSize,
        sort: sort.length > 0 ? sort : undefined,
        ...nextFilterState,
      };

      dispatch(getOrganizationsListAction(apiParams));
    },
    [dispatch],
  );

  const initialState = useMemo(() => getInitialStateWithSorters(columns), [columns]);

  const { tableState, onTableChange, filterState, filterCount, onFilterChange, onItemRemove } =
    useTableSearchParams(initialState, getTableItems, organizations.length, total, hiddenColumns);

  useEffect(() => {
    return () => {
      dispatch(clearState());
    };
  }, [dispatch]);

  const showSider = useCallback(() => {
    setSiderVisible(true);
  }, []);

  const hideSider = useCallback(() => {
    setSiderVisible(false);
  }, []);

  const onLoadFilterOptions = async (fieldName: string) => {
    const filterFieldsCopy = [...filterFields];
    const loadingIndex = filterFieldsCopy.findIndex((field) => field.name === fieldName);
    if (loadingIndex < 0) return;

    filterFieldsCopy[loadingIndex].loading = true;
    setFilterFields([...filterFieldsCopy]);

    // Пока не реализуем загрузку опций для фильтров
    // В municipalities здесь загружаются опции регионов
    // Для organizations можно будет добавить загрузку муниципалитетов

    filterFieldsCopy[loadingIndex].loading = false;
    setFilterFields([...filterFieldsCopy]);
  };

  return {
    state: {
      organizations,
      total,
      loading,
      columns,
      filteredColumns,
      visibleKeys,
      siderVisible,
      filterFields,
      tableState,
      filterState,
      filterCount,
    },
    methods: {
      setVisibleKeys,
      onTableChange,
      showSider,
      hideSider,
      setFilterFields,
      onFilterChange,
      onItemRemove,
      onLoadFilterOptions,
    },
  };
};
